import { PropsWithChildren, useState, useRef, useEffect } from 'react';

import { hashData, debounce } from '../01-core/Utilities';
import { Tabs } from '../03-molecules/Tabs';

export interface TabbedContentItemProps extends PropsWithChildren {
  /** The tab label */
  label: string;
  /** Stable id used as anchor target for hash deep-links */
  id?: string;
}

export interface TabbedContentProps {
  /** The tabs */
  items: TabbedContentItemProps[];
  /** The current tab */
  current?: number;
  /** Id of the tab to open, matched against item ids (hash deep-link) */
  activeId?: string;
  /** Additional class */
  className?: string;
  /** On tab change event handler */
  onTabChange?: (tab: number) => void;
}

export interface TabbedContentInternalItemProps extends TabbedContentItemProps {
  /** The unique ID of the tab content */
  id: string;
  /** The tab properties */
  tab: {
    label: string;
    'aria-controls': string;
    'aria-expanded': 'true' | 'false';
  };
}

/** Block rendering a tabbed content block */
export const TabbedContent = ({
  items,
  current = -1,
  activeId,
  className,
  onTabChange,
}: TabbedContentProps) => {
  const resolveActiveTab = () => {
    if (activeId) {
      const index = items.findIndex((item) => item.id === activeId);
      if (index >= 0) return index;
    }
    return Math.max(current, 0);
  };
  const [tab, setTab] = useState(resolveActiveTab);
  const [enableScroll, setEnableScroll] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const id = 'tab-' + hashData(items);

  // State managed by parent
  if (current !== -1 && current !== tab) {
    setTab(current);
  }

  // Open the tab referenced by a hash deep-link (without blocking manual switches)
  useEffect(() => {
    if (!activeId) return;
    const index = items.findIndex((item) => item.id === activeId);
    if (index >= 0) {
      setTab(index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  /**
   * Check if tabs need scrolling based on content width
   */
  const checkScrollNeeded = () => {
    if (tabsRef.current) {
      const tabsContainer = tabsRef.current.querySelector(
        '.tabs',
      ) as HTMLElement;
      if (tabsContainer) {
        const { scrollWidth, clientWidth } = tabsContainer;
        const needsScroll = scrollWidth > clientWidth;
        setEnableScroll(needsScroll);
      }
    }
  };

  const handleResize = debounce(
    () => {
      checkScrollNeeded();
    },
    100,
    false,
  );

  // Effect to check scroll necessity on mount and items change
  useEffect(() => {
    // Delay to ensure DOM is fully rendered
    setTimeout(() => {
      checkScrollNeeded();
    }, 200);
  }, [items]);

  // Effect to handle resize events
  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const tabs = items.map((t, i) => {
    const item = { ...t } as TabbedContentInternalItemProps;
    item.id = t.id ?? `${id}-${i + 1}`;
    item.tab = {
      label: item.label,
      'aria-controls': item.id,
      'aria-expanded': tab == i ? 'true' : 'false',
    };

    return item;
  });

  return (
    <div className={['tabbed-content', className].join(' ')}>
      <div ref={tabsRef}>
        <Tabs
          className="tabbed-content__tabs"
          currentTab={tab}
          items={tabs.map((item) => item.tab)}
          enableScroll={enableScroll}
          onTabChange={(tab) => {
            setTab(tab);
            if (onTabChange) {
              onTabChange(tab);
            }
          }}
        />
      </div>
      <div className="tabbed-content__wrapper">
        {tabs.map((item, i) => (
          <div
            key={i}
            id={item.id}
            className={[
              'tabbed-content__content',
              tab == i ? 'tabbed-content__content--current' : '',
            ].join(' ')}
            aria-hidden={tab == i ? 'true' : 'false'}
          >
            {item.children}
          </div>
        ))}
      </div>
    </div>
  );
};
